import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import type { SyncLog } from '../types/integration.types';

interface Props {
  log: SyncLog | null;
  onClose: () => void;
}

/**
 * Normaliza errorDetails: puede ser el objeto rico de ExternalHttpError
 * ({ message, status, method, url, response, hint }), un { message } simple,
 * o un array de errores por registro (sync PARTIAL).
 */
export function describeSyncError(details: any): {
  message: string;
  status?: number;
  method?: string;
  url?: string;
  response?: string;
  hint?: string;
  items?: string[];
} {
  if (!details) return { message: 'Sin detalle registrado' };
  if (Array.isArray(details)) {
    return {
      message: `${details.length} registro(s) con error`,
      items: details.map((d) =>
        typeof d === 'string'
          ? d
          : (d?.error || d?.message || JSON.stringify(d)) + (d?.sapId ? ` (${d.sapId})` : '')
      ),
    };
  }
  if (typeof details === 'string') return { message: details };
  return {
    message: details.message || JSON.stringify(details),
    status: details.status,
    method: details.method,
    url: details.url,
    response: details.response,
    hint: details.hint,
  };
}

function Row({
  label,
  children,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 text-sm">
      <div className="text-muted-foreground">{label}</div>
      <div className={mono ? 'font-mono text-xs break-all' : 'break-words'}>{children}</div>
    </div>
  );
}

export function SyncErrorDialog({ log, onClose }: Props) {
  const d = log ? describeSyncError(log.errorDetails) : null;

  return (
    <Dialog open={!!log} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Detalle del error de sincronización
            {d?.status && <Badge variant="destructive">HTTP {d.status}</Badge>}
          </DialogTitle>
          <DialogDescription>
            {log && (
              <>
                {log.direction === 'INBOUND' ? 'Importacion' : 'Exportacion'} &middot;{' '}
                {new Date(log.startedAt).toLocaleString('es')} &middot;{' '}
                {log.triggeredBy === 'scheduled' ? 'Programado' : 'Manual'}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {d && (
          <div className="space-y-3">
            {d.hint && (
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 text-sm">
                <span className="font-medium">Que revisar: </span>
                {d.hint}
              </div>
            )}

            {d.url && (
              <Row label="URL pedida" mono>
                {d.method} {d.url}
              </Row>
            )}
            {d.response !== undefined && d.response !== '' && (
              <Row label="Respondio">
                <span className="italic">&ldquo;{d.response}&rdquo;</span>
              </Row>
            )}
            <Row label="Mensaje">{d.message}</Row>

            {d.items && (
              <div className="max-h-64 overflow-auto rounded-md border p-2 text-xs font-mono space-y-1">
                {d.items.map((it, i) => (
                  <div key={i}>{it}</div>
                ))}
              </div>
            )}

            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">JSON completo</summary>
              <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-muted p-2 whitespace-pre-wrap break-all">
                {JSON.stringify(log?.errorDetails, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
